import * as React from "react";
import { TableCell, TableRow } from "@mui/material";
import { ISearchparamUI, ITableUI } from "./tableTemplate";
import {
  Header,
  ISearchParameters,
  ITableHeaderState,
  ITableReport,
  Report,
  TableHeaderState,
} from "../../Interfaces/TableInterfaces";
import Table from "../../Components/Table";
import CheckBox from "../../Components/Checkbox";
import SouthIcon from "@mui/icons-material/South";
import NorthIcon from "@mui/icons-material/North";
import Cell, { unCapitalizeFirstLetter } from "./Cell";
import Button from "../../Components/Button";
import { Edit, Timeline } from "@mui/icons-material";
import { Modal, StaticDropdown } from "src/Components";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";

// import SortIcon from "@mui/icons-material/Sort";

import "../../Components/CompactOrderPreviewTable.css";

interface IHoveredCellTooltip {
  columnName: string;
  rowName: string | number;
  rect: DOMRect;
}

export const CommonSearchParam: React.FC<ISearchparamUI<ISearchParameters>> = ({
  intialValue,
  onSubmit,
}: ISearchparamUI<ISearchParameters>) => {
  return <></>;
};

interface ICommonTableUIProps<
  TRow extends ITableReport,
> extends ITableUI<TRow> {
  footerRowClassName?: string;
  renderCell?: (row: TRow, header: Header) => React.ReactNode | undefined;
  getRowClassName?: (row: TRow, index: number) => string;
}

export const CommonTableUI = <TRow extends ITableReport>({
  reports,
  AdditionalBody,
  onAction,
  tableHeaderClassName,
  tableBoxClassName,
  withSelect,
  isRowSelectable,
  stickySelectColumn,
  onTableRowClick,
  isRowClickable,
  onTableHeaderClick,
  onCellClick,
  onBulkCancelClick,
  sortingType,
  setSortingType,
  showHiddenCols,
  getExtraTypeUI,
  hideWhenNoResp,
  filterCols,
  refreshFlag,
  RightSideAdditionalBody,
  activeRowId,
  activeRowClassName,
  isCellActive,
  onSelectedRowsChange,
  scrollKey = "#",
  showCellHoverTooltip,
  footerRowClassName = "bg-slate-50",
  renderCell,
  getRowClassName,
}: ICommonTableUIProps<TRow>) => {
  const headerStateLocalStorageKey =
    "tableHeaderStates" + filterCols?.tableName;

  const [selectedRowsIds, setSelectedRowsIdsState] = React.useState<number[]>(
    [],
  );
  const setSelectedRowsIds = React.useCallback(
    (value: number[]) => {
      setSelectedRowsIdsState(value);
      onSelectedRowsChange?.(value);
    },
    [onSelectedRowsChange],
  );
  const [
    isTableHeaderCustomizationModalOpen,
    setIsTableHeaderCustomizationModalOpen,
  ] = React.useState<boolean>(false);
  const [hoveredCellTooltip, setHoveredCellTooltip] =
    React.useState<IHoveredCellTooltip | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const fixedFirstOffsetClass = stickySelectColumn ? "!right-8" : "!right-0";

  const [tableHeaderStates, setTableHeaderStates] = React.useState(
    {} as ITableHeaderState,
  );

  React.useEffect(() => {
    const storedScroll = sessionStorage.getItem(scrollKey);
    if (storedScroll && scrollRef.current) {
      const { x, y } = JSON.parse(storedScroll);
      scrollRef.current.scrollLeft = x;
      scrollRef.current.scrollTop = y;
    }
  }, [scrollKey]);
  React.useEffect(() => {
    if (filterCols && filterCols.show && !Object.keys(tableHeaderStates).length)
      getTableHeaderStates();
  }, [reports]);

  function getTableHeaderStates() {
    let result = {} as ITableHeaderState;

    if (localStorage.getItem(headerStateLocalStorageKey)) {
      const storedState = JSON.parse(
        localStorage.getItem(headerStateLocalStorageKey)!,
      ) as ITableHeaderState;
      result = { ...result, ...storedState };
    }

    setTableHeaderStates(result);
  }

  const handleScroll = () => {
    setHoveredCellTooltip(null);

    if (scrollRef.current) {
      sessionStorage.setItem(
        scrollKey,
        JSON.stringify({
          x: scrollRef.current.scrollLeft,
          y: scrollRef.current.scrollTop,
        }),
      );
    }
  };

  const isRowSelected = (id: number) => {
    if (!selectedRowsIds) return false;
    const res = selectedRowsIds.find((i) => i === id);
    if (res) return true;
    return false;
  };

  React.useEffect(() => {
    if (refreshFlag && onAction) onAction();
  }, [refreshFlag]);

  React.useEffect(() => {
    if (!showCellHoverTooltip) return;

    const clearTooltipIfModalOpen = () => {
      if (document.querySelector(".modal-container")) {
        setHoveredCellTooltip(null);
      }
    };

    clearTooltipIfModalOpen();

    const observer = new MutationObserver(clearTooltipIfModalOpen);
    observer.observe(document.body, { childList: true });

    return () => observer.disconnect();
  }, [showCellHoverTooltip]);

  const selectCheckBoxChangeHandler = (value: boolean, rowId: number) => {
    if (!selectedRowsIds) {
      setSelectedRowsIds([]);
    }
    if (value) {
      setSelectedRowsIds([...selectedRowsIds, rowId]);
    }
    if (!value) {
      const res = selectedRowsIds?.filter((id) => id !== rowId);
      if (res) {
        setSelectedRowsIds(res);
      } else {
        setSelectedRowsIds(selectedRowsIds);
      }
    }
  };

  const getFixedFirstCols = (): Report<TRow> => ({
    ...reports,
    translatedHeaders: reports.translatedHeaders.filter(
      (header) => header.positionTypeStr === "FixedFirst",
    ),
  });

  const getFilteredCols = (): Report<TRow> => ({
    ...reports,
    translatedHeaders: reports.translatedHeaders
      .map((theader) => ({
        ...theader,
        positionTypeStr: Object.keys(tableHeaderStates).length
          ? tableHeaderStates[theader.headerName] !== TableHeaderState.Auto
            ? tableHeaderStates[theader.headerName] || theader.positionTypeStr
            : theader.positionTypeStr
          : theader.positionTypeStr,
      }))
      .filter(
        (f) =>
          f.positionTypeStr !== TableHeaderState.Hidden &&
          [...(filterCols?.allowedPositions || []), "FirstPage"].includes(
            f.positionTypeStr || "",
          ),
      )
      .map((h) => ({
        ...h,
        classname:
          h.positionTypeStr === "DefaultHidden"
            ? "!bg-sky-200"
            : h.positionTypeStr === "Mediator"
              ? "!bg-red-200"
              : "",
      })),
  });

  const getMiddleCols = (): Report<TRow> =>
    filterCols
      ? getFilteredCols()
      : {
          ...reports,
          translatedHeaders: reports.translatedHeaders.filter((header) =>
            showHiddenCols
              ? header.positionTypeStr === "FirstPage" ||
                header.positionTypeStr === "DefaultHidden"
              : header.positionTypeStr === "FirstPage",
          ),
        };
  const getFixedLastCols = (): Report<TRow> => ({
    ...reports,
    translatedHeaders: reports.translatedHeaders.filter(
      (header) => header.positionTypeStr === "FixedLast",
    ),
  });

  const getTableCellTooltipValues = (theader: Header, clonedRow: TRow) => {
    return {
      columnName: theader.translateHeaderName || theader.headerName,
      rowName: clonedRow.rowName || clonedRow.row,
    };
  };

  const getTableCellJSX = (
    theader: Header,
    clonedRow: TRow,
    className?: string,
  ) => {
    const rowValueMap = clonedRow as Record<string, any>;
    const cName =
      rowValueMap[unCapitalizeFirstLetter(theader.headerName) + "Classname"];
    const cellStyle =
      rowValueMap[unCapitalizeFirstLetter(theader.headerName) + "Style"];
    const cellActive = isCellActive ? isCellActive(clonedRow, theader) : false;
    const customCell = renderCell?.(clonedRow, theader);
    return (
      <TableCell
        key={theader.headerName + clonedRow.id}
        style={cellStyle}
        className={`${cName || ""} ${className || ""} ${
          showCellHoverTooltip ? "!relative" : ""
        } ${onCellClick ? "cursor-pointer" : ""} ${
          cellActive ? "!bg-blue-300 !font-bold" : ""
        }`}
        onClick={
          onCellClick
            ? (event) => {
                event.stopPropagation();
                onCellClick(clonedRow, theader);
              }
            : undefined
        }
        onMouseEnter={(event) => {
          if (!showCellHoverTooltip) return;

          setHoveredCellTooltip({
            ...getTableCellTooltipValues(theader, clonedRow),
            rect: event.currentTarget.getBoundingClientRect(),
          });
        }}
        onMouseLeave={() => {
          if (showCellHoverTooltip) setHoveredCellTooltip(null);
        }}
      >
        <div className="flex flex-row justify-center items-center gap-2 !w-full">
          {customCell !== undefined ? customCell : getExtraTypeUI ? (
            theader.extraTypeStr === "NoExtra" ? (
              <Cell
                onAction={onAction}
                clonedRow={clonedRow}
                headerName={theader.headerName}
                type={theader.typeStr}
              />
            ) : (
              getExtraTypeUI({
                row: clonedRow,
                extraType: theader.extraTypeStr,
                headerName: theader.headerName,
                cellType: theader.typeStr || "",
                translateHeaderName: theader.translateHeaderName,
              })
            )
          ) : (
            <Cell
              onAction={onAction}
              clonedRow={clonedRow}
              headerName={theader.headerName}
              type={theader.typeStr}
            />
          )}
        </div>
      </TableCell>
    );
  };

  const GetRow = (_: Report<TRow>, row: TRow) => {
    const clonedRow = JSON.parse(JSON.stringify(row));
    const fixedFirst = getFixedFirstCols().translatedHeaders.map((theader) =>
      getTableCellJSX(
        theader,
        clonedRow,
        `!z-10 !bg-sky-50 !border-0 ${
          theader.translateHeaderName === "#" ? "" : "inner-fixed-column"
        }`,
      ),
    );
    const middle = getMiddleCols().translatedHeaders.map((theader) => {
      return getTableCellJSX(theader, clonedRow);
    });
    const fixedLast = getFixedLastCols().translatedHeaders.map((theader) => {
      return getTableCellJSX(
        theader,
        clonedRow,
        `sticky left-0 !z-10 !bg-sky-50 !border-0 ${
          theader.translateHeaderName === "#" ? "" : "inner-fixed-column"
        }`,
      );
    });

    return (
      <>
        {RightSideAdditionalBody && onAction ? (
          <RightSideAdditionalBody.getRow
            row={row}
            onAction={onAction}
            selectedRowsIds={selectedRowsIds}
            setSelectedRowsIds={setSelectedRowsIds}
          />
        ) : (
          <></>
        )}
        {getFixedFirstCols().translatedHeaders.length ? (
          <TableCell
            className={`!sticky ${fixedFirstOffsetClass} !z-50 !p-0 fixed-column bg-sky-50`}
          >
            {fixedFirst}
          </TableCell>
        ) : (
          <></>
        )}

        {middle}
        {getFixedLastCols().translatedHeaders.length ? (
          <TableCell className="!sticky !left-0 !z-50 fixed-column">
            {fixedLast}
          </TableCell>
        ) : (
          <></>
        )}
        {AdditionalBody && onAction ? (
          <AdditionalBody.getRow
            row={row}
            onAction={onAction}
            selectedRowsIds={selectedRowsIds}
            setSelectedRowsIds={setSelectedRowsIds}
          />
        ) : (
          <></>
        )}
      </>
    );
  };

  const getEmptyRow = () => {
    const fixedFirst = getFixedFirstCols().translatedHeaders.map((header) => (
      <TableCell
        key={`empty-fixed-first-${header.headerName}`}
        className="!text-center"
      >
        -
      </TableCell>
    ));
    const middle = getMiddleCols().translatedHeaders.map((header) => (
      <TableCell
        key={`empty-middle-${header.headerName}`}
        className="!text-center"
      >
        -
      </TableCell>
    ));
    const fixedLast = getFixedLastCols().translatedHeaders.map((header) => (
      <TableCell
        key={`empty-fixed-last-${header.headerName}`}
        className="!text-center"
      >
        -
      </TableCell>
    ));

    return (
      <TableRow className="h-10 !w-full">
        {withSelect ? (
          <TableCell
            className={`!text-center ${
              stickySelectColumn
                ? "!sticky !right-0 !z-[60] !w-8 !min-w-8 !max-w-8 !p-0 !bg-sky-50"
                : ""
            }`}
          >
            -
          </TableCell>
        ) : null}
        {getFixedFirstCols().translatedHeaders.length ? (
          <TableCell
            className={`!sticky ${fixedFirstOffsetClass} !z-50 !p-0 fixed-column bg-sky-50`}
          >
            {fixedFirst}
          </TableCell>
        ) : null}
        {middle}
        {getFixedLastCols().translatedHeaders.length ? (
          <TableCell className="!sticky !left-0 !z-50 fixed-column">
            {fixedLast}
          </TableCell>
        ) : null}
      </TableRow>
    );
  };

  const getSelectableRows = () =>
    isRowSelectable
      ? reports.report.filter((row) => isRowSelectable(row))
      : reports.report;

  const isAllRowsSelected = () => {
    if (!selectedRowsIds) return false;
    const selectableRows = getSelectableRows();
    if (!selectableRows.length) return false;
    if (selectableRows.length === selectedRowsIds.length) return true;
    return false;
  };

  const onSelectAllChange = (value: boolean) => {
    if (!value) setSelectedRowsIds([]);
    else {
      const res = getSelectableRows().map((report) => report.id);
      setSelectedRowsIds(res);
    }
  };

  const tableHeaderClickHandler = (header: Header) => {
    if ((sortingType || sortingType === "") && setSortingType)
      setSortingType(header.headerName);
    if (onTableHeaderClick) onTableHeaderClick(header);
  };

  const getHeadersSortingIcon = (theader: Header) => {
    if (!sortingType) return null;

    const sortingTypeParts = sortingType.split("_");
    const sortingTypeHeaderName = sortingTypeParts.slice(0, -1).join("_"); // gets every part except last ones
    if (theader.headerName === sortingTypeHeaderName) {
      if (sortingTypeParts[sortingTypeParts.length - 1] === "desc")
        return <SouthIcon className="!text-xs" />;
      if (sortingTypeParts[sortingTypeParts.length - 1] === "asc")
        return <NorthIcon className="!text-xs" />;
    }
  };

  const getTableHeaderJSX = (theader: Header, className?: string) => (
    <TableCell
      key={theader.headerName}
      className={`!cursor-pointer bg-white hover:!bg-slate-200 !transition-all ${theader.classname} ${className} ${tableHeaderClassName}`}
      onClick={() => tableHeaderClickHandler(theader)}
    >
      <span className="!font-black text-black">
        {theader.translateHeaderName}
      </span>
      {theader.headerSubtitle ? (
        <div className="!text-[10px] !font-normal !leading-tight text-slate-600">
          {theader.headerSubtitle}
        </div>
      ) : null}
      {theader.extraTypeStr === "WithChart" ? (
        <Timeline fontSize="small" className="!text-gray-700" />
      ) : (
        <></>
      )}
      {getHeadersSortingIcon(theader)}
    </TableCell>
  );

  const header = () => {
    const r = (
      <>
        {withSelect ? (
          <TableCell
            key="selectRowCheckBox"
            className={`!p-0 ${
              stickySelectColumn
                ? "!sticky !right-0 !z-[110] !w-8 !min-w-8 !max-w-8 !bg-white"
                : ""
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {reports && reports.report?.length ? (
              <CheckBox
                checked={isAllRowsSelected()}
                label=""
                name=""
                onChange={(name, value) => onSelectAllChange(value)}
              />
            ) : null}
          </TableCell>
        ) : null}
        {reports &&
        reports.translatedHeaders &&
        getFixedFirstCols().translatedHeaders.length ? (
          <TableCell
            className={`!sticky ${fixedFirstOffsetClass} !z-[100] fixed-column`}
          >
            {getFixedFirstCols().translatedHeaders.map((theader) => {
              return getTableHeaderJSX(
                theader,
                `!bg-sky-50 !border-0 !flex-1 ${
                  theader.translateHeaderName === "#"
                    ? ""
                    : "inner-fixed-column"
                }`,
              );
            })}
          </TableCell>
        ) : (
          <></>
        )}
        {reports &&
          reports.translatedHeaders &&
          getMiddleCols().translatedHeaders.map((theader) => {
            return getTableHeaderJSX(theader);
          })}
        {reports &&
        reports.translatedHeaders &&
        getFixedLastCols().translatedHeaders.length ? (
          <TableCell className="!sticky !left-0 !z-100 fixed-column">
            {getFixedLastCols().translatedHeaders.map((theader) => {
              return getTableHeaderJSX(theader, "!bg-sky-50 !border-0 !flex-1");
            })}
          </TableCell>
        ) : (
          <></>
        )}
      </>
    );

    if (AdditionalBody && onAction) {
      return (
        <>
          {r}
          <AdditionalBody.getHeader
            onAction={onAction}
            selectedRowsIds={selectedRowsIds}
            setSelectedRowsIds={setSelectedRowsIds}
            isSelectAll={
              selectedRowsIds && reports
                ? selectedRowsIds.length === reports.report.length
                : false
            }
          />
        </>
      );
    }

    if (RightSideAdditionalBody && onAction) {
      return (
        <>
          <RightSideAdditionalBody.getHeader
            onAction={onAction}
            selectedRowsIds={selectedRowsIds}
            setSelectedRowsIds={setSelectedRowsIds}
            isSelectAll={
              selectedRowsIds && reports
                ? selectedRowsIds.length === reports.report.length
                : false
            }
          />
          {r}
        </>
      );
    }

    return <>{r}</>;
  };

  const tableRowClickHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    row: TRow,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (onTableRowClick) onTableRowClick(row);
  };

  const body = (
    <>
      {reports && reports.translatedHeaders && reports.report.length ? (
        reports.report.map((row, index) => (
          <TableRow
            key={row.id}
            selected={isRowSelected(row.id)}
            className={`!transition-all !w-full !relative ${
              !isRowClickable || isRowClickable(row)
                ? "hover:!bg-slate-200 cursor-pointer"
                : "cursor-default"
            } ${
              activeRowId && activeRowId === row.id ? activeRowClassName ?? "!bg-blue-100" : ""
            } ${getRowClassName?.(row, index) ?? ""}`}
            onClick={
              !isRowClickable || isRowClickable(row)
                ? (e) => tableRowClickHandler(e, row)
                : undefined
            }
          >
            {withSelect ? (
              <TableCell
                className={
                  stickySelectColumn
                    ? "!sticky !right-0 !z-[60] !w-8 !min-w-8 !max-w-8 !p-0 !bg-white"
                    : ""
                }
                onClick={(e) => e.stopPropagation()}
              >
                <CheckBox
                  checked={!!isRowSelected(row.id)}
                  label=""
                  name=""
                  disabled={isRowSelectable ? !isRowSelectable(row) : false}
                  onChange={(name, value) =>
                    selectCheckBoxChangeHandler(value, row.id)
                  }
                />
              </TableCell>
            ) : null}
              {GetRow(reports, row)}
            </TableRow>
          ))
      ) : (
        getEmptyRow()
      )}
      {reports?.report.length && reports?.footer ? (
        reports.footer.map((row) => (
          <TableRow
            key={row.row}
            className={`${footerRowClassName} !transition-all !w-full !relative`}
          >
            {GetRow(reports, row)}
          </TableRow>
        ))
      ) : (
        <></>
      )}
    </>
  );

  const tableHeaderStateOptions: DropdownItem[] = [
    { name: "مخفی", id: TableHeaderState.Hidden },
    { name: "جزئیات", id: TableHeaderState.DefaultHidden },
    { name: "اتوماتیک", id: TableHeaderState.Auto },
    { name: "محاسباتی", id: TableHeaderState.Mediator },
    { name: "صفحه اصلی", id: TableHeaderState.FirstPage },
  ];

  const handleTableHeaderStateChange = (
    headerName: string,
    state: TableHeaderState,
  ) => {
    setTableHeaderStates((prev) => ({
      ...prev,
      [headerName]: state,
    }));
  };

  const handleSubmitHeaderStates = () => {
    localStorage.setItem(
      headerStateLocalStorageKey,
      JSON.stringify(tableHeaderStates),
    );
    setIsTableHeaderCustomizationModalOpen(false);
  };

  const tableHeaderCustomizationUI = (
    <div className="flex flex-col justify-start items-start gap-8">
      {reports &&
      reports.translatedHeaders &&
      reports.translatedHeaders.length ? (
        <div className="flex flex-col justify-start items-start gap-3 max-h-[400px] overflow-y-auto w-full">
          {reports.translatedHeaders
            .filter(
              (header) =>
                header.positionTypeStr !== "FixedFirst" &&
                header.positionTypeStr !== "FixedLast",
            )
            .map((header) => (
              <div
                key={header.headerName}
                className="flex justify-start items-center w-full gap-5"
              >
                <div className="mr-2 whitespace-nowrap">
                  {header.translateHeaderName}
                </div>
                <div className="mr-auto w-40">
                  <StaticDropdown
                    options={tableHeaderStateOptions}
                    onChange={(_, value) =>
                      handleTableHeaderStateChange(header.headerName, value)
                    }
                    dropdown={{
                      name: "",
                      placeholder: "وضعیت",
                    }}
                    value={
                      tableHeaderStates[header.headerName] ||
                      TableHeaderState.Auto
                    }
                  />
                </div>
              </div>
            ))}
        </div>
      ) : (
        <></>
      )}
      <div className="flex flex-row justify-start items-center gap-3 w-full">
        <Button
          text="پیش فرض"
          onClick={() => {
            localStorage.removeItem(headerStateLocalStorageKey);
            setTableHeaderStates({} as ITableHeaderState);
            setIsTableHeaderCustomizationModalOpen(false);
          }}
          variant="outlined"
        />

        <Button
          text="ذخیره تغییرات"
          onClick={handleSubmitHeaderStates}
          variant="contained"
        />
      </div>
    </div>
  );

  if (!reports) return null;
  if (!reports.count && hideWhenNoResp) return <></>;
  return (
    <div className="w-full flex flex-col justify-start items-end gap-5">
      {hoveredCellTooltip ? (
        <>
          <div
            className="pointer-events-none fixed z-[399] -translate-x-1/2 -translate-y-full whitespace-nowrap rounded border border-white/20 bg-slate-900/75 px-2 py-1 text-xs text-white shadow-lg backdrop-blur-md"
            style={{
              left:
                hoveredCellTooltip.rect.left +
                hoveredCellTooltip.rect.width / 2,
              top: hoveredCellTooltip.rect.top - 6,
            }}
          >
            {hoveredCellTooltip.columnName}
            <span
              className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2"
              style={{
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: "6px solid rgba(15, 23, 42, 0.75)",
              }}
            />
          </div>
          <div
            className="pointer-events-none fixed z-[399] -translate-y-1/2 whitespace-nowrap rounded border border-white/20 bg-slate-900/75 px-2 py-1 text-xs text-white shadow-lg backdrop-blur-md"
            style={{
              left: hoveredCellTooltip.rect.right + 6,
              top:
                hoveredCellTooltip.rect.top +
                hoveredCellTooltip.rect.height / 2,
            }}
          >
            {hoveredCellTooltip.rowName}
            <span
              className="absolute right-full top-1/2 h-0 w-0 -translate-y-1/2"
              style={{
                borderTop: "6px solid transparent",
                borderBottom: "6px solid transparent",
                borderRight: "6px solid rgba(15, 23, 42, 0.75)",
              }}
            />
          </div>
        </>
      ) : (
        <></>
      )}
      {isTableHeaderCustomizationModalOpen ? (
        <Modal
          title="تنظیم ستون‌ها"
          UI={tableHeaderCustomizationUI}
          onClose={() => setIsTableHeaderCustomizationModalOpen(false)}
        />
      ) : (
        <></>
      )}
      {filterCols && filterCols.show ? (
        <div
          className="absolute -top-2 -right-2 bg-sky-600 w-6 h-6 !text-white rounded-full flex justify-center items-center z-[101] cursor-pointer"
          onClick={() => setIsTableHeaderCustomizationModalOpen(true)}
        >
          <Edit
            onClick={() => setIsTableHeaderCustomizationModalOpen(true)}
            fontSize="small"
          />
        </div>
      ) : (
        <></>
      )}
      <div
        className={`w-full m-auto border-2 border-sky-100 rounded overflow-x-auto ${tableBoxClassName}`}
        onScroll={handleScroll}
        ref={scrollRef}
      >
        <Table header={header()} body={body} />
      </div>
      {withSelect && onBulkCancelClick ? (
        <Button
          onClick={() => {
            if (onBulkCancelClick) onBulkCancelClick(selectedRowsIds);
          }}
          text="حذف گروهی"
          variant="contained"
          disabled={!!!selectedRowsIds.length}
          className={`${!!!selectedRowsIds.length ? "" : "red-btn"} max-w-min`}
        />
      ) : (
        <></>
      )}
    </div>
  );
};
